import AdminMenu from '../AdminMenu';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FiBellOff,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiHelpCircle,
  FiLogOut,
  FiMail,
  FiMessageSquare,
  FiUsers,
} from 'react-icons/fi';
import Navbar from '../Navbar';
import { useTranslation } from "react-i18next";
import LogoutConfirmation from 'pages/Logout/LogoutConfirmationModel';
import { useAuth } from 'context/auth-context';
import { PageHeader } from 'components/ui';

const rowClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 px-4 min-h-[56px] text-sm font-medium transition-colors hover:bg-surface-hover ${
    isActive ? 'text-primary-text bg-primary-soft/60' : 'text-ink'
  }`;

const RowIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center justify-center rounded w-9 h-9 shrink-0 bg-surface-hover text-ink-2" aria-hidden="true">
    {children}
  </span>
);

const RowContent = ({ icon, label }: { icon: React.ReactNode; label: React.ReactNode }) => (
  <>
    <RowIcon>{icon}</RowIcon>
    <span className="flex-1 min-w-0 truncate">{label}</span>
    <FiChevronRight className="w-4 h-4 shrink-0 text-ink-4" aria-hidden="true" />
  </>
);

const GroupLabel = ({ children }: { children: React.ReactNode }) => (
  <h2 className="px-1 mb-2 text-xs font-semibold tracking-wider uppercase text-ink-3">{children}</h2>
);

const groupClass = 'overflow-hidden divide-y zs-card divide-line';

export default function MoreSub() {
  const { auth, roleLoading } = useAuth();
  const { t } = useTranslation();
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  return (
    <>
      <div>
        <Navbar />
      </div>

      <div className="block lg:hidden zs-page zs-page-narrow">
        <PageHeader eyebrow="Menu" title="More" subtitle="Your activity, preferences and support in one place." />

        <div className="flex flex-col gap-6">
          <section>
            <GroupLabel>Activity</GroupLabel>
            <ul className={groupClass}>
              <li>
                <NavLink to="/my-referral" className={rowClass}>
                  <RowContent icon={<FiUsers className="w-4 h-4" />} label={t("side.bar.Friend")} />
                </NavLink>
              </li>
              <li>
                <NavLink to="/history" className={rowClass}>
                  <RowContent icon={<FiClock className="w-4 h-4" />} label={t("side.bar.History")} />
                </NavLink>
              </li>
              <li>
                <NavLink to="/wallet-history" className={rowClass}>
                  <RowContent icon={<FiCreditCard className="w-4 h-4" />} label={'Wallet History'} />
                </NavLink>
              </li>
            </ul>
          </section>

          <section>
            <GroupLabel>Preferences</GroupLabel>
            <ul className={groupClass}>
              {/* Email preference centre (§7). Placed immediately before the legacy
                  Unsubscribe entry, which it supersedes: that screen flips a single
                  mutable boolean, this one manages the three purposes
                  independently and writes an audit row per change. */}
              <li>
                <NavLink to="/email-preferences" className={rowClass}>
                  <RowContent icon={<FiMail className="w-4 h-4" />} label={'Email Preferences'} />
                </NavLink>
              </li>
              <li>
                <NavLink to="/unsubscribe" className={rowClass}>
                  <RowContent icon={<FiBellOff className="w-4 h-4" />} label={t("side.bar.Unsubscribe")} />
                </NavLink>
              </li>
            </ul>
          </section>

          <section>
            <GroupLabel>Support</GroupLabel>
            <ul className={`${groupClass} [&_.admin-navigation-mobile]:border-0 [&_.admin-navigation-mobile]:rounded-none`}>
              {Number(auth?.role) === 2 && <li><AdminMenu mobile /></li>}
              {!roleLoading && Number(auth?.role) !== 2 && <li>
                <NavLink to="/my-queries" className={rowClass}>
                  <RowContent icon={<FiMessageSquare className="w-4 h-4" />} label="My queries" />
                </NavLink>
              </li>}
              <li>
                <NavLink to="/raise-query" className={rowClass}>
                  <RowContent icon={<FiHelpCircle className="w-4 h-4" />} label={t("side.bar.Help")} />
                </NavLink>
              </li>
            </ul>
          </section>

          <ul className={groupClass}>
            <li
              onClick={() => setShowLogoutPopup(true)}
              className="flex items-center gap-3 px-4 min-h-[56px] text-sm font-medium cursor-pointer text-danger transition-colors hover:bg-danger-soft"
            >
              <span className="inline-flex items-center justify-center rounded w-9 h-9 shrink-0 bg-danger-soft text-danger" aria-hidden="true">
                <FiLogOut className="w-4 h-4" />
              </span>
              <span className="flex-1">{t("side.bar.Logout")}</span>
            </li>
          </ul>
        </div>
      </div>
      {showLogoutPopup && <LogoutConfirmation onClose={() => setShowLogoutPopup(false)} />}
    </>
  );
}
